import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Camera,
  Clipboard,
  Usb,
  AlertTriangle,
  Grid3x3,
  Monitor,
} from 'lucide-react';
import dayjs from 'dayjs';
import { getActivityTimeline } from '../services/api';
import { PageHeader } from '../components/ui/page-header';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { cn } from '../lib/utils';

const TIMELINE_HOURS = 24;

const TYPE_ICONS = {
  screenshot: Camera,
  clipboard: Clipboard,
  usb: Usb,
  incident: AlertTriangle,
  application: Monitor,
};

function getEventIcon(type) {
  return TYPE_ICONS[type] || Grid3x3;
}

function getEventTone(type, riskScore) {
  if (type === 'incident') return 'bg-destructive/10 text-destructive ring-destructive/30';
  if (type === 'application') return 'bg-success/10 text-success ring-success/30';
  if (riskScore >= 50) return 'bg-warning/10 text-warning ring-warning/30';
  if (riskScore > 0) return 'bg-primary/10 text-primary ring-primary/30';
  return 'bg-muted text-muted-foreground ring-border';
}

function TimelinePage() {
  const { workerId } = useParams();
  const workerIdNum = workerId ? parseInt(workerId, 10) : null;
  const [activities, setActivities] = useState([]);

  useEffect(() => {
    loadActivities();
    const interval = setInterval(loadActivities, 15000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workerId]);

  const loadActivities = async () => {
    try {
      const response = await getActivityTimeline(
        TIMELINE_HOURS,
        workerIdNum != null && !Number.isNaN(workerIdNum) ? workerIdNum : null
      );
      setActivities(response.data.data);
    } catch (err) {
      console.error('Failed to load timeline:', err);
    }
  };

  return (
    <div className="mx-auto flex max-w-[1400px] flex-col gap-6">
      <PageHeader
        title="Activity Timeline"
        description={
          workerIdNum != null && !Number.isNaN(workerIdNum)
            ? 'Chronological view for this worker (applications, screenshots, clipboard, USB, incidents).'
            : 'Chronological view of all monitoring events.'
        }
      />

      {activities.length === 0 ? (
        <Alert variant="info">
          <AlertDescription>No activities recorded yet</AlertDescription>
        </Alert>
      ) : (
        <Card>
          <CardContent className="p-6">
            <ol className="relative flex flex-col gap-6">
              {activities.map((activity, index) => {
                const Icon = getEventIcon(activity.type);
                const toneClasses = getEventTone(activity.type, activity.risk_score);
                return (
                  <li key={index} className="flex gap-4">
                    <div className="flex w-24 flex-shrink-0 flex-col items-end text-xs text-muted-foreground">
                      <span>{dayjs(activity.timestamp).format('HH:mm:ss')}</span>
                      <span>{dayjs(activity.timestamp).format('MMM DD')}</span>
                    </div>
                    <div className="relative flex flex-col items-center">
                      <div
                        className={cn(
                          'flex h-9 w-9 items-center justify-center rounded-full ring-1',
                          toneClasses
                        )}
                      >
                        <Icon className="h-4 w-4" />
                      </div>
                      {index < activities.length - 1 && (
                        <div className="absolute top-9 h-[calc(100%+1rem)] w-px bg-border" />
                      )}
                    </div>
                    <div className="flex-1 pb-2">
                      <p className="text-sm text-foreground">{activity.description}</p>
                      <div className="mt-1.5 flex flex-wrap items-center gap-2">
                        <Badge variant="outline" className="capitalize">
                          {activity.type}
                        </Badge>
                        {activity.risk_score > 0 && (
                          <Badge
                            variant={activity.risk_score >= 50 ? 'destructive' : 'warning'}
                          >
                            Risk: {activity.risk_score}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default TimelinePage;
